import { ref, reactive, onActivated, nextTick } from 'vue';
import { ElMessage, ElMessageBox } from 'element-plus';
import { Plus, Edit, Delete } from '@element-plus/icons-vue';
import { request } from '@/util/globalContext';
import { listCommon } from '@/util/vueutil/commonList.js';

export default function useNoticeList() {
  const listRef = ref(null);
  const dialogVisible = ref(false);
  const dialogType = ref('add');
  const formRef = ref(null);

  const searchForm = reactive({
    content: '',
    status: ''
  });

  const {
    listShow,
    handleReset,
    handleSelectionChange,
    handleCurrentRowChange
  } = listCommon(searchForm);

  const FORM_DEFAULT = {
    id: '',
    content: '',
    isNew: 0,
    sort: 99,
    status: '1',
    effectType: '2'
  };

  const formData = reactive({ ...FORM_DEFAULT });

  const formRules = {
    content: [
      { required: true, message: '请输入公告内容', trigger: 'blur' }
    ],
    sort: [
      { required: true, message: '请输入排序', trigger: 'blur' }
    ],
    status: [
      { required: true, message: '请选择状态', trigger: 'change' }
    ]
  };

  const handleLoadList = async (paginationParams) => {
    if (!listRef.value) return;
    if (!listRef.value.isInited) {
      await nextTick();
      if (!listRef.value?.isInited) {
        ElMessage.warning('列表组件初始化中，请稍后重试');
        return;
      }
    }

    listRef.value?.setLoading(true);
    try {
      const params = {
        ...paginationParams,
        content: searchForm.content,
        status: searchForm.status
      };
      const res = await request.get('/preset/notice/getPage', { params });
      if (listRef.value) {
        if (res.code === 200) {
          listRef.value.updateListData(res.data.records || []);
          listRef.value.updatePagination({
            pageNum: paginationParams.pageNum,
            pageSize: paginationParams.pageSize,
            total: res.data.total || 0
          });
        } else {
          ElMessage.error('列表加载失败：' + (res.msg || '接口异常'));
          listRef.value.updateListData([]);
          listRef.value.updatePagination({ ...paginationParams, total: 0 });
        }
      }
    } catch (error) {
      ElMessage.error('列表加载失败：网络异常');
      if (listRef.value) {
        listRef.value.updateListData([]);
        listRef.value.updatePagination({ ...paginationParams, total: 0 });
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleAdd = async () => {
    dialogType.value = 'add';
    formRef.value?.resetFields();
    Object.assign(formData, JSON.parse(JSON.stringify(FORM_DEFAULT)));
    dialogVisible.value = true;
  };

  const handleEdit = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择一条数据！');
      return;
    }

    try {
      listRef.value?.setLoading(true);
      const res = await request.get('/preset/notice/getDetail', { params: { id: listShow.value.id } });
      if (res.code !== 200) {
        ElMessage.error(res.msg || '接口异常');
        return;
      }

      formData.id = res.data.id || '';
      formData.content = res.data.content || '';
      formData.isNew = res.data.isNew ?? 0;
      formData.sort = res.data.sort ?? 99;
      formData.status = res.data.status || '1';

      dialogType.value = 'edit';
      dialogVisible.value = true;
    } catch (error) {
      ElMessage.error('获取详情失败：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!listShow.value) {
      ElMessage.warning('请先选择数据！');
      return;
    }

    try {
      await ElMessageBox.confirm('确定删除选中的数据？', '删除确认', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      });

      listRef.value?.setLoading(true);
      const params = { id: listShow.value.id };
      const res = await request.post('/preset/notice/delete', params);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success('删除成功');
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          ElMessage.error('删除失败：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      if (error !== 'cancel') {
        ElMessage.error('删除失败：网络异常');
      }
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formRef.value) return;

    const isValid = await new Promise((resolve) => {
      formRef.value.validate((valid) => {
        resolve(valid);
      });
    });
    if (!isValid) return;

    const submitData = {
      id: dialogType.value === 'edit' ? formData.id : '',
      content: formData.content,
      isNew: formData.isNew,
      sort: formData.sort,
      status: formData.status,
      effectType: formData.effectType
    };

    listRef.value?.setLoading(true);
    try {
      const res = await request.post('/preset/notice/saveOrUpdate', submitData);
      if (listRef.value) {
        if (res.code === 200) {
          ElMessage.success(dialogType.value === 'add' ? '新增成功' : '修改成功');
          dialogVisible.value = false;
          const pagination = listRef.value.getPagination();
          handleLoadList(pagination);
        } else {
          const tip = dialogType.value === 'add' ? '新增失败' : '修改失败';
          ElMessage.error(tip + '：' + (res.msg || '接口异常'));
        }
      }
    } catch (error) {
      ElMessage.error('接口异常：' + error);
    } finally {
      listRef.value?.setLoading(false);
    }
  };

  onActivated(async () => {
    if (listRef.value?.isInited) {
      const pagination = listRef.value.getPagination();
      handleLoadList(pagination);
    }
  });

  return {
    searchForm,
    listRef,
    dialogVisible,
    dialogType,
    formRef,
    listShow,
    formData,
    formRules,
    Plus,
    Edit,
    Delete,
    handleSelectionChange,
    handleCurrentRowChange,
    handleAdd,
    handleEdit,
    handleDelete,
    handleLoadList,
    handleReset,
    handleSubmit
  };
}
